import cx from 'classnames';
import { ReactNode, Children, isValidElement, cloneElement, ReactElement } from 'react';

import { XOR } from '../../../shared/utilities/types/XOR';

import { ListItem, OrderedListItemProps, UnorderedListItemProps } from './ListItem';

import style from './List.module.css';

type ListItemPropsType = React.ComponentProps<typeof ListItem>;

type Props = XOR<{ items: string[] }, { children: ReactNode }> & {
    className?: string;
    classes?: {
        marker?: string;
        content?: string;
    };
} & (Omit<OrderedListItemProps, 'index'> | Omit<UnorderedListItemProps, 'index'>);

export function List({
    items,
    children,
    className,
    ordered,
    outlined,
    BulletIcon,
    classes,
}: Props) {
    if (items && children) {
        console.warn(
            'List: You should provide either items or children, not both. Items will be displayed before children.',
        );
    }

    const ListElement = ordered ? 'ol' : 'ul';

    return (
        <ListElement className={cx(style.list, className)}>
            {items
                ? items.map((item, index) => {
                      const props = ordered ? { ordered, outlined, index } : { BulletIcon };
                      return (
                          <ListItem key={`item-${index}`} {...props} classes={classes}>
                              {item}
                          </ListItem>
                      );
                  })
                : null}
            {children
                ? Children.map(children, (child, index) => {
                      if (isValidElement(child) && child.type === ListItem) {
                          const listItem = child as ReactElement<ListItemPropsType>;

                          const props = ordered
                              ? {
                                    ...listItem.props,
                                    ordered: true as const,
                                    outlined,
                                    index,
                                    BulletIcon: undefined,
                                }
                              : {
                                    ...listItem.props,
                                    BulletIcon: listItem.props.BulletIcon ?? BulletIcon,
                                    ordered: undefined,
                                    outlined: undefined,
                                    index: undefined,
                                };

                          const mergedClasses: Record<string, string | undefined> = {};
                          const definedClassKeys = new Set([
                              ...Object.keys(classes ?? {}),
                              ...Object.keys(listItem.props.classes ?? {}),
                          ]);
                          definedClassKeys.forEach((key) => {
                              // If the child ListItem has a prop of that classKey, we want this one to be applied
                              // Else, we apply the List level one
                              mergedClasses[key] =
                                  listItem.props.classes?.[
                                      key as keyof typeof listItem.props.classes
                                  ] || classes?.[key as keyof typeof classes];
                          });

                          return cloneElement(listItem, {
                              key: `child-${index}`,
                              ...props,
                              classes:
                                  Object.keys(mergedClasses).length > 0 ? mergedClasses : undefined,
                          });
                      }
                      console.warn(
                          `List: All children should be ListItem components. Child at index ${index} will be ignored.`,
                      );
                      return null;
                  })
                : null}
        </ListElement>
    );
}

// Re-export ListItem for easier import
export { ListItem } from './ListItem';
